import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  FaLifeRing, FaPlus, FaTrash, FaSave, FaEye, FaEyeSlash, FaSync, FaUndo,
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const ICON_OPTIONS = [
  'FaHeadset', 'FaRocket', 'FaBook', 'FaDollarSign', 'FaBuilding', 'FaMapMarkerAlt',
  'FaBan', 'FaFileAlt', 'FaQuestionCircle', 'FaCreditCard', 'FaClock', 'FaExchangeAlt',
  'FaGlobeAmericas', 'FaIdCard', 'FaClipboardCheck', 'FaUniversity', 'FaBalanceScale',
  'FaUndo', 'FaShieldAlt', 'FaUsers', 'FaChartLine', 'FaCheckCircle', 'FaLightbulb',
  'FaHandshake', 'FaStar', 'FaCog', 'FaEnvelope', 'FaPhone', 'FaComments', 'FaSearch',
  'FaInfoCircle', 'FaExclamationTriangle', 'FaLock', 'FaBriefcase', 'FaPassport',
  'FaLandmark', 'FaGavel', 'FaFileInvoiceDollar', 'FaPercent', 'FaWallet', 'FaReceipt', 'FaUserTie',
];

const GRADIENT_OPTIONS = [
  'from-[#3b82f6] to-[#1d4ed8]',
  'from-[#8b5cf6] to-[#6d28d9]',
  'from-[#10b981] to-[#059669]',
  'from-[#f59e0b] to-[#d97706]',
  'from-[#ef4444] to-[#dc2626]',
  'from-[#06b6d4] to-[#0891b2]',
];

const emptySection = (type = 'card', index = 0) => ({
  sectionType: type,
  title: '',
  description: '',
  category: '',
  text: '',
  link: '',
  icon: 'FaFileAlt',
  gradient: GRADIENT_OPTIONS[0],
  items: [],
  order: index,
  isActive: true,
});

const emptyRelated = (index) => ({
  title: '',
  description: '',
  link: '',
  icon: 'FaFileAlt',
  order: index,
  isActive: true,
});

const HelpCenterCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [pageList, setPageList] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('help');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({
    badge: '', title: '', subtitle: '', author: '', updatedAtLabel: '',
    breadcrumbLabel: '', heroIcon: 'FaHeadset', bodyHtml: '', isPublished: true,
    sections: [], relatedArticles: [],
    cta: { title: '', description: '', buttonText: '', link: '', icon: 'FaHeadset' },
    pageType: 'hub',
  });

  const fetchList = async () => {
    const token = localStorage.getItem('adminToken');
    const res = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/cms/help/admin/list`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (res.data.success) setPageList(res.data.data);
  };

  const fetchPage = async (slug) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/help/admin/${slug}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const d = res.data.data;
        setForm({
          badge: d.badge || '',
          title: d.title || '',
          subtitle: d.subtitle || '',
          author: d.author || '',
          updatedAtLabel: d.updatedAtLabel || '',
          breadcrumbLabel: d.breadcrumbLabel || '',
          heroIcon: d.heroIcon || 'FaHeadset',
          bodyHtml: d.bodyHtml || '',
          isPublished: d.isPublished !== false,
          pageType: d.pageType || 'hub',
          sections: (d.sections || []).map((s, i) => ({
            _id: s._id,
            sectionType: s.sectionType || 'card',
            title: s.title || '',
            description: s.description || '',
            category: s.category || '',
            text: s.text || '',
            link: s.link || '',
            icon: s.icon || 'FaFileAlt',
            gradient: s.gradient || GRADIENT_OPTIONS[0],
            order: typeof s.order === 'number' ? s.order : i,
            isActive: s.isActive !== false,
            items: (s.items || []).map((item, j) => ({
              _id: item._id,
              title: item.title || '',
              text: item.text || '',
              description: item.description || '',
              link: item.link || '',
              icon: item.icon || '',
              order: typeof item.order === 'number' ? item.order : j,
              isActive: item.isActive !== false,
            })),
          })),
          relatedArticles: (d.relatedArticles || []).map((r, i) => ({
            _id: r._id,
            title: r.title || '',
            description: r.description || '',
            link: r.link || '',
            icon: r.icon || 'FaFileAlt',
            order: typeof r.order === 'number' ? r.order : i,
            isActive: r.isActive !== false,
          })),
          cta: {
            title: d.cta?.title || '',
            description: d.cta?.description || '',
            buttonText: d.cta?.buttonText || '',
            link: d.cta?.link || '',
            icon: d.cta?.icon || 'FaHeadset',
          },
        });
      }
    } catch (err) {
      toastError('Failed to load help page');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList().then(() => fetchPage(selectedSlug));
  }, []);

  useEffect(() => {
    if (selectedSlug) fetchPage(selectedSlug);
  }, [selectedSlug]);

  const updateField = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const updateCta = (field, value) => setForm((p) => ({ ...p, cta: { ...p.cta, [field]: value } }));

  const updateSection = (index, field, value) => {
    setForm((p) => {
      const sections = [...p.sections];
      sections[index] = { ...sections[index], [field]: value };
      return { ...p, sections };
    });
  };

  const updateSectionItem = (sIndex, iIndex, field, value) => {
    setForm((p) => {
      const sections = [...p.sections];
      const items = [...(sections[sIndex].items || [])];
      items[iIndex] = { ...items[iIndex], [field]: value };
      sections[sIndex] = { ...sections[sIndex], items };
      return { ...p, sections };
    });
  };

  const addSection = (type) => {
    setForm((p) => ({ ...p, sections: [...p.sections, emptySection(type, p.sections.length)] }));
  };

  const removeSection = (index) => {
    if (!window.confirm('Remove this section?')) return;
    setForm((p) => ({
      ...p,
      sections: p.sections.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })),
    }));
  };

  const addSectionItem = (sIndex) => {
    setForm((p) => {
      const sections = [...p.sections];
      const items = [...(sections[sIndex].items || []), { text: '', title: '', link: '', order: sections[sIndex].items?.length || 0, isActive: true }];
      sections[sIndex] = { ...sections[sIndex], items };
      return { ...p, sections };
    });
  };

  const updateRelated = (index, field, value) => {
    setForm((p) => {
      const relatedArticles = [...p.relatedArticles];
      relatedArticles[index] = { ...relatedArticles[index], [field]: value };
      return { ...p, relatedArticles };
    });
  };

  const addRelated = () => setForm((p) => ({ ...p, relatedArticles: [...p.relatedArticles, emptyRelated(p.relatedArticles.length)] }));
  const removeRelated = (index) => {
    if (!window.confirm('Remove related article?')) return;
    setForm((p) => ({ ...p, relatedArticles: p.relatedArticles.filter((_, i) => i !== index) }));
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/help/seed`,
        { reloadContent: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess(res.data.message || 'All pages synced');
        await fetchList();
        await fetchPage(selectedSlug);
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleReloadDefaults = async () => {
    if (!window.confirm('Restore default content for this page? Your edits will be overwritten.')) return;
    setSyncing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/help/admin/${selectedSlug}/reload-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Default content loaded');
        fetchPage(selectedSlug);
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reload defaults');
    } finally {
      setSyncing(false);
    }
  };

  const pageTypeLabel = (type) => {
    if (type === 'article') return 'Article';
    if (type === 'form') return 'Form';
    if (type === 'hub') return 'Hub';
    return type || 'Page';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/help/${selectedSlug}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Help page saved successfully');
        fetchPage(selectedSlug);
        fetchList();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const groupedPages = pageList.reduce((acc, p) => {
    const g = p.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(p);
    return acc;
  }, {});

  return (
    <PageLayout>
      <PageHeader
        title="Help Center CMS"
        description={`Edit all ${pageList.length || 20} Help Center pages — hubs, articles, and submit form`}
        icon={FaLifeRing}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value)}
              className="input-modern py-2.5 text-sm min-w-[280px] max-w-md"
            >
              {Object.entries(groupedPages).map(([group, pages]) => (
                <optgroup key={group} label={`${group} (${pages.length})`}>
                  {pages.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      [{pageTypeLabel(p.pageType)}] {p.title || p.slug}
                    </option>
                  ))}
                </optgroup>
              ))}
              {pageList.length === 0 && <option value="help">Help Center</option>}
            </select>
            <button onClick={handleSyncAll} disabled={syncing || loading} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors flex items-center gap-2">
              <FaSync className="w-3.5 h-3.5" /> {syncing ? 'Syncing...' : 'Sync All Pages'}
            </button>
            <button onClick={handleReloadDefaults} disabled={syncing || loading} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors flex items-center gap-2">
              <FaUndo className="w-3.5 h-3.5" /> Reload Defaults
            </button>
            <button onClick={handleSave} disabled={saving || loading} className="btn-premium flex items-center gap-2">
              <FaSave className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : (
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page Header</h2>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isPublished} onChange={(e) => updateField('isPublished', e.target.checked)} />
                Published
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase text-gray-500">Badge</label>
                <input className="input-modern" value={form.badge} onChange={(e) => updateField('badge', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase text-gray-500">Hero Icon</label>
                <select className="input-modern" value={form.heroIcon} onChange={(e) => updateField('heroIcon', e.target.value)}>
                  {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase text-gray-500">Breadcrumb Label</label>
                <input className="input-modern" value={form.breadcrumbLabel} onChange={(e) => updateField('breadcrumbLabel', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase text-gray-500">Title</label>
              <input className="input-modern" value={form.title} onChange={(e) => updateField('title', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase text-gray-500">Subtitle</label>
              <textarea className="input-modern min-h-[80px]" value={form.subtitle} onChange={(e) => updateField('subtitle', e.target.value)} />
            </div>
            {form.pageType === 'article' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase text-gray-500">Author</label>
                  <input className="input-modern" value={form.author} onChange={(e) => updateField('author', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase text-gray-500">Last Updated Label</label>
                  <input className="input-modern" value={form.updatedAtLabel} onChange={(e) => updateField('updatedAtLabel', e.target.value)} />
                </div>
              </div>
            )}
          </div>

          {form.pageType === 'article' && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Article Body (HTML)</h2>
              <p className="text-sm text-gray-500">Leave empty to use the built-in page layout. When filled, this replaces the article content on the website.</p>
              <div className="bg-white dark:bg-dark-900 rounded-xl overflow-hidden border border-gray-200 dark:border-dark-600">
                <ReactQuill theme="snow" value={form.bodyHtml} onChange={(v) => updateField('bodyHtml', v)} className="h-72 pb-12" />
              </div>
            </div>
          )}

          {(form.pageType === 'hub' || form.pageType === 'form') && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Sections / Cards / Links</h2>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => addSection('card')} className="text-sm text-primary-600 px-3 py-1 rounded-lg hover:bg-primary-50">+ Card</button>
                  <button onClick={() => addSection('topicGroup')} className="text-sm text-primary-600 px-3 py-1 rounded-lg hover:bg-primary-50">+ Topic Group</button>
                  <button onClick={() => addSection('resourceCategory')} className="text-sm text-primary-600 px-3 py-1 rounded-lg hover:bg-primary-50">+ Resource Category</button>
                  <button onClick={() => addSection('link')} className="text-sm text-primary-600 px-3 py-1 rounded-lg hover:bg-primary-50">+ Link</button>
                </div>
              </div>
              {form.sections.map((section, sIndex) => (
                <div key={section._id || sIndex} className="rounded-xl border border-gray-200 dark:border-dark-600 p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm">{section.sectionType} #{sIndex + 1}</span>
                    <div className="flex gap-2">
                      <button onClick={() => updateSection(sIndex, 'isActive', !section.isActive)} className="p-2 text-gray-500">
                        {section.isActive ? <FaEye /> : <FaEyeSlash />}
                      </button>
                      <button onClick={() => removeSection(sIndex)} className="p-2 text-red-400"><FaTrash /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <input className="input-modern text-sm" placeholder="Title" value={section.title} onChange={(e) => updateSection(sIndex, 'title', e.target.value)} />
                    <input className="input-modern text-sm" placeholder="Category" value={section.category} onChange={(e) => updateSection(sIndex, 'category', e.target.value)} />
                    <input className="input-modern text-sm" placeholder="Link text" value={section.text} onChange={(e) => updateSection(sIndex, 'text', e.target.value)} />
                    <input className="input-modern text-sm" placeholder="Link URL" value={section.link} onChange={(e) => updateSection(sIndex, 'link', e.target.value)} />
                    <select className="input-modern text-sm" value={section.icon} onChange={(e) => updateSection(sIndex, 'icon', e.target.value)}>
                      {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                    <select className="input-modern text-sm" value={section.gradient} onChange={(e) => updateSection(sIndex, 'gradient', e.target.value)}>
                      {GRADIENT_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <textarea className="input-modern text-sm min-h-[60px]" placeholder="Description" value={section.description} onChange={(e) => updateSection(sIndex, 'description', e.target.value)} />
                  {(section.sectionType === 'topicGroup' || section.sectionType === 'resourceCategory') && (
                    <div className="space-y-2 pl-4 border-l-2 border-primary-200">
                      <button onClick={() => addSectionItem(sIndex)} className="text-xs text-primary-600">+ Add item</button>
                      {(section.items || []).map((item, iIndex) => (
                        <div key={iIndex} className="grid grid-cols-2 gap-2">
                          <input className="input-modern text-sm" placeholder="Item title/text" value={item.title || item.text} onChange={(e) => updateSectionItem(sIndex, iIndex, section.sectionType === 'topicGroup' ? 'text' : 'title', e.target.value)} />
                          <input className="input-modern text-sm" placeholder="Link" value={item.link} onChange={(e) => updateSectionItem(sIndex, iIndex, 'link', e.target.value)} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {form.pageType === 'article' && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
              <div className="flex justify-between">
                <h2 className="text-lg font-bold">Related Articles</h2>
                <button onClick={addRelated} className="text-sm text-primary-600 flex items-center gap-1"><FaPlus /> Add</button>
              </div>
              {form.relatedArticles.map((rel, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 rounded-xl bg-gray-50 dark:bg-dark-900">
                  <input className="input-modern text-sm" placeholder="Title" value={rel.title} onChange={(e) => updateRelated(index, 'title', e.target.value)} />
                  <input className="input-modern text-sm" placeholder="Link" value={rel.link} onChange={(e) => updateRelated(index, 'link', e.target.value)} />
                  <select className="input-modern text-sm" value={rel.icon} onChange={(e) => updateRelated(index, 'icon', e.target.value)}>
                    {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                  <div className="flex gap-2">
                    <input className="input-modern text-sm flex-1" placeholder="Description" value={rel.description} onChange={(e) => updateRelated(index, 'description', e.target.value)} />
                    <button onClick={() => removeRelated(index)} className="text-red-400 p-2"><FaTrash /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {form.pageType !== 'article' && (
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
              <h2 className="text-lg font-bold">Contact CTA Block</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input className="input-modern" placeholder="CTA Title" value={form.cta.title} onChange={(e) => updateCta('title', e.target.value)} />
                <select className="input-modern" value={form.cta.icon} onChange={(e) => updateCta('icon', e.target.value)}>
                  {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <textarea className="input-modern min-h-[70px]" placeholder="CTA Description" value={form.cta.description} onChange={(e) => updateCta('description', e.target.value)} />
              <div className="grid grid-cols-2 gap-4">
                <input className="input-modern" placeholder="Button text" value={form.cta.buttonText} onChange={(e) => updateCta('buttonText', e.target.value)} />
                <input className="input-modern" placeholder="Button link" value={form.cta.link} onChange={(e) => updateCta('link', e.target.value)} />
              </div>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default HelpCenterCMS;
